<template>
	<fieldset
		class="mx-auto flex w-full flex-col items-center"
		:disabled="disabled"
	>
		<legend class="mb-4 w-full text-center text-3xl">
			{{ $t("payment_method_select_title") }}
		</legend>
		<div
			role="radiogroup"
			:aria-label="$t('payment_method_select_title')"
			class="flex w-full flex-col items-center"
		>
			<label
				v-for="name in adapters"
				:key="name"
				class="my-2 inline-flex w-full cursor-pointer items-center gap-3 rounded-xs bg-white p-4 shadow transition-shadow md:max-w-2xl has-[:checked]:shadow-md"
				:aria-disabled="disabled"
			>
				<span class="relative flex items-center">
					<input
						:id="`payment-adapter-${name}`"
						type="radio"
						name="payment-adapter"
						class="peer h-5 w-5 cursor-pointer appearance-none rounded-full border border-slate-200 bg-slate-100 transition-all checked:border-slate-300"
						:value="name"
						:checked="modelValue === name"
						:disabled="disabled"
						@change="handleSelect(name)"
					/>
					<span
						class="pointer-events-none absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-800 opacity-0 transition-opacity duration-200 peer-checked:opacity-100"
					></span>
				</span>
				<!-- With a valid logo, show only the logo (its alt text carries the
				name for screen readers) — no redundant text label next to it. -->
				<NuxtImg
					v-if="hasLogo(name)"
					:src="paymentLogos[name]"
					:alt="labelFor(name)"
					:width="80"
					:height="24"
					class="h-6 w-auto max-w-[80px] object-contain"
					loading="lazy"
					@error="handleLogoError(name)"
				/>
				<template v-else>
					<DocumentTextIcon
						v-if="name === 'ManualInvoice'"
						class="h-5 w-5 shrink-0 text-slate-600"
						aria-hidden="true"
					/>
					<span class="text-sm text-slate-600">
						{{ labelFor(name) }}
					</span>
				</template>
			</label>
		</div>
	</fieldset>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { DocumentTextIcon } from "@heroicons/vue/24/outline";

interface Props {
	modelValue: string;
	adapters: string[];
	disabled?: boolean;
}
withDefaults(defineProps<Props>(), { disabled: false });

const emit = defineEmits<{ "update:modelValue": [value: string] }>();

const { t, te } = useI18n();

// Provider logos live in `public/images/payment/`. Adapters without a logo
// (e.g. ManualInvoice) fall back to an icon + label.
const paymentLogos: Record<string, string> = {
	Klarna: "/images/payment/klarna-logo.png",
	Svea: "/images/payment/sveabank-logo.png",
	Walley: "/images/payment/walley-logo.png"
};

// Track logos that fail to load so the card falls back to its text label.
const brokenLogos = ref<Set<string>>(new Set());
const handleLogoError = (name: string) => {
	brokenLogos.value = new Set(brokenLogos.value).add(name);
};
const hasLogo = (name: string) =>
	Boolean(paymentLogos[name]) && !brokenLogos.value.has(name);

const labelFor = (name: string) => {
	const key = `payment_adapter_${name}`;
	return te(key) ? t(key) : name;
};

const handleSelect = (name: string) => {
	emit("update:modelValue", name);
};
</script>
